// components/Admin/SessionManager.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Quiz, SessionState, Participant, LeaderboardEntry } from '../../types/quiz.types';
import {
  getQuiz,
  getCorrectAnswers,
  startSession,
  advanceQuestion,
  publishLeaderboard,
  openAnswers,
  closeAnswers,
  revealAnswer,
  setSessionYoutube,
  setSessionShow,
  setSessionDelayOptions,
  publishShoutout,
  finalizeSession,
  subscribeToSession,
  subscribeToLeaderboard,
  subscribeToAnswerCounts,
  subscribeToParticipants,
  serverNow,
} from '../../services/live-session';
import { toEmbedUrl } from '../../utils/youtube';
import { rankChip } from '../../constants/answer-tiles';
import { useAuth } from '../../context/Auth/auth-context';

export const SessionManager = () => {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const { user, userInfo } = useAuth();
  const [session, setSession] = useState<SessionState | null>(null);
  const [participants, setParticipants] = useState<Array<{ uid: string } & Participant>>([]);
  const [ranks, setRanks] = useState<LeaderboardEntry[]>([]);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [correct, setCorrect] = useState<number[]>([]);
  const [answersByUser, setAnswersByUser] = useState<Record<string, Record<string, number>>>({});
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [showDraft, setShowDraft] = useState('');
  const [delayDraft, setDelayDraft] = useState(false);
  const [lastShoutout, setLastShoutout] = useState<string | null>(null);
  // Two-step confirms for mis-click safety while presenting live: first tap
  // arms, second tap (within 3s) fires. 'end' always arms; 'next' arms only
  // when advancing would skip a question nobody answered.
  const [armed, setArmed] = useState<'end' | 'next' | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!sessionCode) return;
    const unsubSession = subscribeToSession(sessionCode, setSession);
    const unsubRanks = subscribeToLeaderboard(sessionCode, setRanks);

    // Participants are denormalized {uid: {name}} — read names directly, no
    // per-user fetch. Admin-only listener; never fanned out to clients.
    const unsubParticipants = subscribeToParticipants(sessionCode, setParticipants);

    // Admin-only: live per-user answers, so the host can see who's answered the
    // current question. Players never subscribe to this path (rules deny it).
    const unsubAnswers = subscribeToAnswerCounts(sessionCode, setAnswersByUser);

    return () => { unsubSession(); unsubRanks(); unsubParticipants(); unsubAnswers(); };
  }, [sessionCode]);

  // Fetch the quiz blob + answer key once. The key never leaves this admin
  // client except as the single revealed index written after answers lock.
  useEffect(() => {
    const quizId = session?.quizId;
    if (!quizId || quiz) return;
    getQuiz(quizId).then((q) => setQuiz(q));
    getCorrectAnswers(quizId).then(setCorrect).catch(() => setCorrect([]));
  }, [session?.quizId, quiz]);

  const totalQuestions = quiz ? quiz.questions.length : null;
  const status = session?.status;
  const isStream = !!session?.youtubeUrl;
  const answersOpen = !!session?.answersOpen;
  const delayOptions = !!session?.delayOptions;
  const currentIndex = session?.currentQuestionIndex ?? 0;
  const revealed = session?.revealedAnswerIndex != null;
  const correctIndex = correct[currentIndex] ?? -1;

  const answeredUids = new Set(
    Object.entries(answersByUser)
      .filter(([, byIdx]) => byIdx?.[currentIndex] != null)
      .map(([uid]) => uid)
  );
  const answeredCount = answeredUids.size;

  // APP MODE host countdown — same derivation the phones use, so the host sees
  // the same clock the players do instead of flying blind.
  useEffect(() => {
    const q = quiz?.questions[currentIndex];
    // With delayOptions the clock hasn't started until the host opens answers —
    // questionStartTime still holds the (stale) advance timestamp until then.
    if (isStream || status !== 'in_progress' || !q || !session?.questionStartTime || (delayOptions && !answersOpen)) {
      setTimeLeft(null);
      return;
    }
    const startTime = session.questionStartTime;
    const tick = () => {
      const elapsed = (serverNow() - startTime) / 1000;
      setTimeLeft(Math.max(0, Math.ceil(q.timer - elapsed)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isStream, status, session?.questionStartTime, quiz, currentIndex, delayOptions, answersOpen]);

  const disarm = useCallback(() => {
    setArmed(null);
    if (armTimer.current) clearTimeout(armTimer.current);
  }, []);

  const arm = (what: 'end' | 'next') => {
    setArmed(what);
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = setTimeout(() => setArmed(null), 3000);
  };

  const copyPin = () => {
    if (!sessionCode) return;
    navigator.clipboard?.writeText(sessionCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleStart = async () => { if (sessionCode) await startSession(sessionCode); };

  const isLastQuestion =
    totalQuestions !== null && currentIndex >= totalQuestions - 1;

  const handleNext = useCallback(async () => {
    if (!sessionCode || !session || busy) return;
    // Stream-mode guard: advancing past a question with zero answers usually
    // means the host never opened the window — make them confirm.
    if (isStream && answeredCount === 0 && armed !== 'next') { arm('next'); return; }
    disarm();
    setBusy(true);
    try {
      await publishLeaderboard(sessionCode, session.quizId); // score the question that just finished
      await advanceQuestion(sessionCode, (session.currentQuestionIndex || 0) + 1);
    } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCode, session, busy, isStream, answeredCount, armed, disarm]);

  const handleEnd = async () => {
    if (!sessionCode || !session || busy) return;
    if (armed !== 'end') { arm('end'); return; }
    disarm();
    setBusy(true);
    try {
      // Scores once, publishes the final board, folds the game into the host's
      // day/week windowed boards, then flips the session to ended.
      await finalizeSession(sessionCode, session.quizId, session.hostUid, session.show);
    } finally { setBusy(false); }
  };

  // FREE LOTTERY MOMENT: put one random player's name on the broadcast. Top-3
  // fame only motivates top players; a random shout-out gives the other 90% a
  // reason to be here. Uses the roster this client already holds — no reads.
  const handleShoutout = async () => {
    if (!sessionCode || participants.length === 0) return;
    const pool = participants.filter((p) => p.uid !== lastShoutout);
    const pick = (pool.length > 0 ? pool : participants)[
      Math.floor(Math.random() * (pool.length > 0 ? pool.length : participants.length))
    ];
    setLastShoutout(pick.uid);
    await publishShoutout(sessionCode, pick);
  };

  // The YouTube URL is a setting, not a headline — it lives behind the gear so the
  // host panel stays clean while presenting. Seed the drafts when opening settings.
  const toggleSettings = () => {
    if (!showSettings) {
      setUrlDraft(session?.youtubeUrl ?? '');
      setShowDraft(session?.show ?? '');
      setDelayDraft(!!session?.delayOptions);
    }
    setShowSettings((v) => !v);
  };
  const handleSaveUrl = async () => {
    if (!sessionCode) return;
    await setSessionYoutube(sessionCode, urlDraft);
    await setSessionShow(sessionCode, showDraft);
    await setSessionDelayOptions(sessionCode, delayDraft);
  };
  const handleClearUrl = async () => {
    if (!sessionCode) return;
    setUrlDraft('');
    await setSessionYoutube(sessionCode, '');
  };
  const draftNotEmbeddable = !!urlDraft.trim() && !toEmbedUrl(urlDraft);

  // STREAM MODE: open the answer window when the question is visibly live on the
  // broadcast, then lock-and-score. Locking also publishes the correct index so
  // the presenter screen + every phone can highlight it (the reveal moment).
  const handleOpen = useCallback(async () => {
    if (sessionCode && !busy) await openAnswers(sessionCode);
  }, [sessionCode, busy]);

  const handleLock = useCallback(async () => {
    if (!sessionCode || !session || busy) return;
    setBusy(true);
    try {
      await closeAnswers(sessionCode, correctIndex >= 0 ? correctIndex : undefined);
      await publishLeaderboard(sessionCode, session.quizId);
    } finally { setBusy(false); }
  }, [sessionCode, session, busy, correctIndex]);

  // APP MODE: phones gate themselves on the timer; the host reveals explicitly
  // once the clock hits zero, then advances.
  const handleReveal = async () => {
    if (!sessionCode || !session || busy || correctIndex < 0) return;
    setBusy(true);
    try {
      await revealAnswer(sessionCode, correctIndex);
      await publishLeaderboard(sessionCode, session.quizId);
    } finally { setBusy(false); }
  };

  // Live-op keyboard shortcuts: O open · L lock & reveal · N next. Ignored while
  // typing in an input (the settings URL field) so keys don't fire mid-edit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || e.metaKey || e.ctrlKey) return;
      if (status !== 'in_progress') return;
      const k = e.key.toLowerCase();
      // O also opens in app mode when delayOptions is on (the "options up" beat).
      // N in app mode ignores answersOpen — there is no lock step there.
      if (k === 'o' && !answersOpen && (isStream || delayOptions)) handleOpen();
      else if (k === 'l' && isStream && answersOpen) handleLock();
      else if (k === 'n' && !isLastQuestion && (!isStream || !answersOpen)) handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [status, isStream, delayOptions, answersOpen, isLastQuestion, handleOpen, handleLock, handleNext]);

  // Ownership guard: rules already reject every host write (and the
  // participant/answer reads) for a non-host, but failing loudly here beats a
  // console full of permission-denied errors if a presenter opens someone
  // else's session URL. Admins can drive any session.
  if (
    session &&
    userInfo?.role === 'presenter' &&
    session.hostUid &&
    session.hostUid !== user
  ) {
    return (
      <div className="max-w-md mx-auto p-10 text-center">
        <h2 className="text-2xl font-bold font-display tracking-tight mb-2">Not your game</h2>
        <p className="text-slate-500">
          This session is run by another host. You can only manage sessions you created —
          host the quiz yourself from <Link to="/admin/quizzes" className="font-semibold underline decoration-2 decoration-accent underline-offset-2">Quizzes</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Join PIN — front and center while waiting; compact once live. */}
      {status === 'not_started' ? (
        <div className="rounded-2xl p-8 text-center text-white bg-ink border-2 border-accent shadow-hard-lg">
          <p className="uppercase tracking-widest text-white/70 text-sm font-semibold">Join at this game PIN</p>
          <div className="text-6xl sm:text-7xl font-display font-extrabold tracking-[0.15em] my-3 tabular-nums text-accent">{sessionCode}</div>
          <button onClick={copyPin} className="btn-ghost text-sm">
            {copied ? '✓ Copied' : 'Copy PIN'}
          </button>
          <p className="mt-4 text-white/80">
            <span className="font-bold text-2xl">{participants.length}</span> player{participants.length === 1 ? '' : 's'} waiting
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 card p-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Game PIN</span>
            <div className="text-2xl font-bold tracking-widest tabular-nums">{sessionCode}</div>
          </div>
          {status === 'in_progress' && totalQuestions !== null && (
            <div className="text-right">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Question</span>
              <div className="font-semibold">{currentIndex + 1} / {totalQuestions}</div>
            </div>
          )}
          {status === 'in_progress' && (
            <div className="text-right">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Answered</span>
              <div className="font-semibold tabular-nums">{answeredCount} / {participants.length}</div>
            </div>
          )}
          {status === 'in_progress' && !isStream && (
            timeLeft !== null ? (
              <div className={`grid place-items-center w-12 h-12 rounded-full text-xl font-extrabold font-display ${timeLeft <= 5 ? 'bg-tile-red text-white animate-pulse' : 'bg-ink text-accent'}`}>
                {timeLeft}
              </div>
            ) : delayOptions && !revealed ? (
              <span className="chip bg-ink/10 text-ink">READING</span>
            ) : null
          )}
          {status === 'in_progress' && isStream && (
            answersOpen ? (
              <span className="chip bg-tile-green text-white">ON AIR</span>
            ) : revealed ? (
              <span className="chip bg-accent text-ink">REVEALED</span>
            ) : (
              <span className="chip bg-ink/10 text-ink">LOCKED</span>
            )
          )}
          {status === 'ended' && (
            <span className="chip bg-ink/10 text-ink">ENDED</span>
          )}
        </div>
      )}

      {/* Mode + presenter link, always visible — the broadcast screen is part of
          the core stream-mode flow, not a setting. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-slate-400">
          <span className="chip bg-ink/10 text-ink">{isStream ? 'STREAM' : 'APP'}</span>
          {status === 'in_progress' && isStream ? (
            <span className="ml-2 text-slate-300">· O open · L lock · N next</span>
          ) : status === 'in_progress' && delayOptions ? (
            <span className="ml-2 text-slate-300">· O open · N next</span>
          ) : null}
        </span>
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/session/${sessionCode}/present`}
            target="_blank"
            className="btn-primary text-sm !py-2"
          >
            Open broadcast screen ↗
          </Link>
          <button
            onClick={toggleSettings}
            className="btn-ghost text-sm !text-slate-700 !bg-slate-100 hover:!bg-slate-200"
            aria-expanded={showSettings}
          >
            Settings
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="card p-4 space-y-3">
          <h3 className="font-display font-semibold text-slate-700">Settings</h3>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400">
            YouTube live URL
          </label>
          <input
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://youtube.com/live/…  ·  blank = app mode"
            className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-ink focus:outline-none text-sm"
          />
          {draftNotEmbeddable ? (
            <p className="text-xs text-amber-600 font-medium">
              This URL can't be embedded — players will get an "open on YouTube" link instead of an in-app video. Use a watch/live/youtu.be link.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              {urlDraft.trim()
                ? 'Stream mode — players see shapes only; the question lives on your broadcast.'
                : 'App mode — question + timer render on each player’s phone.'}
            </p>
          )}
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-400 pt-1">
            Show tag <span className="normal-case font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={showDraft}
            onChange={(e) => setShowDraft(e.target.value)}
            placeholder="fun-friday  ·  scopes the day/week champion boards"
            maxLength={40}
            className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-ink focus:outline-none text-sm"
          />
          <p className="text-xs text-slate-400">
            Sessions with the same tag share a day/week leaderboard on your public page. Blank = your main board.
          </p>
          <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={delayDraft}
              onChange={(e) => setDelayDraft(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-ink"
            />
            <span className="text-sm">
              <span className="font-semibold">Read the question first</span>
              <span className="block text-xs text-slate-400 mt-0.5">
                Options stay hidden until you press Open answers (O) — so nobody can
                guess before you've read the question. In app mode the countdown also
                starts at open.
              </span>
            </span>
          </label>
          <div className="flex gap-2">
            <button onClick={handleSaveUrl} className="btn-primary text-sm">Save</button>
            {isStream && (
              <button onClick={handleClearUrl} className="btn-ghost text-sm !text-slate-700 !bg-slate-100 hover:!bg-slate-200">
                Clear (switch to app mode)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Players — pills turn green once they've answered the current question. */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">
            Players ({participants.length})
            {status === 'in_progress' && participants.length > 0 && (
              <span className="ml-2 text-sm font-medium text-slate-400">
                {answeredCount} answered this question
              </span>
            )}
          </h3>
          {status === 'in_progress' && participants.length > 0 && (
            <button
              onClick={handleShoutout}
              className="btn-ghost text-sm !text-slate-700 !bg-slate-100 hover:!bg-slate-200 shrink-0"
              title="Flash a random player's name on the broadcast screen"
            >
              📣 Random shout-out
            </button>
          )}
        </div>
        {participants.length === 0 ? (
          <p className="text-slate-400 text-sm">No one has joined yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {participants.map((p) => {
              const answered = status === 'in_progress' && answeredUids.has(p.uid);
              return (
                <span
                  key={p.uid}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${answered ? 'bg-tile-green/15 text-tile-green-deep ring-1 ring-tile-green' : 'bg-slate-100'}`}
                >
                  {answered ? '✓ ' : ''}{p.avatar ? `${p.avatar} ` : ''}{p.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex gap-3">
        {status === 'not_started' && (
          <button onClick={handleStart} disabled={participants.length === 0} className="btn-success flex-1 text-lg">
            Start game
          </button>
        )}
        {status === 'in_progress' && isStream && (
          <>
            {answersOpen ? (
              <button onClick={handleLock} disabled={busy} className="btn-primary flex-1 text-lg">
                {busy ? 'Scoring…' : 'Lock & reveal'}
              </button>
            ) : (
              <>
                <button onClick={handleOpen} className="btn-success flex-1 text-lg">
                  Open answers
                </button>
                {!isLastQuestion && (
                  <button
                    onClick={handleNext}
                    disabled={busy}
                    className={`flex-1 text-lg ${armed === 'next' ? 'btn-danger' : 'btn-primary'}`}
                  >
                    {busy ? 'Scoring…' : armed === 'next' ? 'No answers yet — tap to skip' : 'Next question →'}
                  </button>
                )}
                <button
                  onClick={handleEnd}
                  disabled={busy}
                  className={`flex-1 text-lg ${armed === 'end' ? '!bg-rose-700 btn-danger' : 'btn-danger'}`}
                >
                  {busy ? 'Scoring…' : armed === 'end' ? 'Tap again to end' : isLastQuestion ? 'Finish & score' : 'End game'}
                </button>
              </>
            )}
          </>
        )}
        {status === 'in_progress' && !isStream && (
          <>
            {delayOptions && !answersOpen && !revealed && (
              <button onClick={handleOpen} className="btn-success flex-1 text-lg">
                Open answers
              </button>
            )}
            {timeLeft === 0 && !revealed && correctIndex >= 0 && (
              <button onClick={handleReveal} disabled={busy} className="btn-success flex-1 text-lg">
                {busy ? 'Scoring…' : 'Reveal answer'}
              </button>
            )}
            {!isLastQuestion && (
              <button onClick={handleNext} disabled={busy} className="btn-primary flex-1 text-lg">
                {busy ? 'Scoring…' : 'Next question →'}
              </button>
            )}
            <button
              onClick={handleEnd}
              disabled={busy}
              className={`flex-1 text-lg ${armed === 'end' ? '!bg-rose-700 btn-danger' : 'btn-danger'}`}
            >
              {busy ? 'Scoring…' : armed === 'end' ? 'Tap again to end' : isLastQuestion ? 'Finish & score' : 'End game'}
            </button>
          </>
        )}
        {status === 'ended' && (
          <div className="flex-1 text-center text-slate-500 font-medium py-3">Game ended.</div>
        )}
      </div>

      {/* Leaderboard preview */}
      <div className="card p-5">
        <h3 className="font-display font-bold mb-3">Leaderboard</h3>
        {ranks.length === 0 ? (
          <p className="text-slate-400 text-sm">
            Scores appear here after you advance each question.
          </p>
        ) : (
          <ol className="space-y-2">
            {ranks.map((e, i) => (
              <li key={e.uid} className={`flex items-center gap-3 p-3 rounded-xl ${i < 3 ? 'bg-accent/15' : 'bg-slate-50'}`}>
                <span className={rankChip(i)}>{i + 1}</span>
                <span className="flex-1 font-medium truncate">{e.avatar ? `${e.avatar} ` : ''}{e.name}</span>
                <span className="font-display font-bold text-ink">{e.score}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};
